import { api } from './client.js';
import type {
  DashboardSummary, DbObjectDetail, DbObjectsResponse, DbSummary, DbJobsResponse, DbJobDetail,
  LoginInput, PasswordChangeInput, Poste, PosteCreateInput, PosteUpdateInput,
  PostesListResponse, UserSession, User, UserUpdateInput, UsersListResponse,
  UserCreateInput, UserCreateResponse, PasswordResetResponse,
  Mesure, MesureUpdateInput, MesureOption, MesuresListResponse,
  IngestInfo, IngestSyncInput, IngestMaintInput,
  JsonServiceInfo, MqttServiceInfo,
  Incident, IncidentsListResponse, IncidentFacets, IncidentListQuery,
  Annotation, AnnotationsListResponse, AnnotationListQuery, AnnotationWriteInput,
  UpdateDataListResponse, UpdateDataFacets, UpdateDataDetail, UpdateDataListQuery, UpdWeewxPurgeResult,
  AuditLogsListResponse, AuditLogDetail, AuditFacets, AuditLogQuery,
  AuthEventsListResponse, AuthEventQuery,
} from './types.js';

// auth
export const authLogin    = (input: LoginInput)            => api.post('/auth/login', input, { confirmed: true });
export const authLogout   = ()                             => api.post('/auth/logout', undefined, { confirmed: true });
export const authMe       = ()                             => api.get<UserSession>('/auth/me').then((r) => r.data);
export const authPwChange = (input: PasswordChangeInput)   => api.post('/auth/password-change', input, { confirmed: true });

// dashboard
export const fetchDashboardSummary = () => api.get<DashboardSummary>('/dashboard/summary').then((r) => r.data);

// postes
export const fetchPostes = (q: { data_source?: number; search?: string; limit?: number } = {}) =>
  api.get<PostesListResponse>('/postes', { params: q }).then((r) => r.data);
export const fetchPoste = (id: number) =>
  api.get<Poste>(`/postes/${id}`).then((r) => r.data);
export const createPosteFn     = (input: PosteCreateInput)                    => api.post<Poste>('/postes', input, { confirmed: true }).then((r) => r.data);
export const updatePosteFn     = (id: number, input: PosteUpdateInput)        => api.put<Poste>(`/postes/${id}`, input, { confirmed: true }).then((r) => r.data);
export const softDeletePosteFn = (id: number)                                 => api.delete<{ ok: true }>(`/postes/${id}`, { confirmed: true }).then((r) => r.data);
export const hardDeletePosteFn = (id: number)                                 => api.delete<{ ok: true }>(`/postes/${id}/hard`, { confirmed: true }).then((r) => r.data);

// admin-db
export const fetchDbSummary = () => api.get<DbSummary>('/admin-db/summary').then((r) => r.data);
export const fetchDbObjects = () => api.get<DbObjectsResponse>('/admin-db/objects').then((r) => r.data);
export const fetchDbObject  = (schema: string, name: string) =>
  api.get<DbObjectDetail>(`/admin-db/objects/${schema}/${name}`).then((r) => r.data);
export const fetchDbJobs    = () => api.get<DbJobsResponse>('/admin-db/jobs').then((r) => r.data);
export const fetchDbJob     = (jobId: number) =>
  api.get<DbJobDetail>(`/admin-db/jobs/${jobId}`).then((r) => r.data);
// run is queued by node_climato (202); pause/resume are immediate (200).
export const runDbJobFn     = (jobId: number) =>
  api.post<IngestQueued>(`/admin-db/jobs/${jobId}/run`, undefined, { confirmed: true }).then((r) => r.data);
export const setDbJobScheduledFn = (jobId: number, scheduled: boolean) =>
  api.post<{ ok: boolean; job_id: number; scheduled: boolean }>(
    `/admin-db/jobs/${jobId}/${scheduled ? 'resume' : 'pause'}`, undefined, { confirmed: true },
  ).then((r) => r.data);
type IngestQueued = { message: string; priorite: number; full: boolean };
export const vacuumDbFn = (full: boolean) =>
  api.post<IngestQueued>('/admin-db/vacuum', { full }, { confirmed: true }).then((r) => r.data);
export const refreshCaggsFn = (scope: 'recent' | 'all') =>
  api.post<IngestQueued>('/admin-db/caggs/refresh', { scope }, { confirmed: true }).then((r) => r.data);
export const chunkCompressFn = (chunk: string, compress: boolean) =>
  api.post<{ ok: boolean }>('/admin-db/chunks/compress', { chunk, compress }, { confirmed: true }).then((r) => r.data);
export const chunksMergeFn = (chunks: string[]) =>
  api.post<{ ok: boolean; merged: string[] }>('/admin-db/chunks/merge', { chunks }, { confirmed: true }).then((r) => r.data);
export const chunkSplitFn = (chunk: string, at: string) =>
  api.post<{ ok: boolean }>('/admin-db/chunks/split', { chunk, at }, { confirmed: true }).then((r) => r.data);
export const chunkOptimizeFn = (hypertable: string, targetMb: number) =>
  api.post<IngestQueued>('/admin-db/chunks/optimize', { hypertable, target_mb: targetMb }, { confirmed: true }).then((r) => r.data);

// services (ingest / sync)
export const fetchIngestInfo = () => api.get<IngestInfo>('/services/ingest/info').then((r) => r.data);
export const ingestSyncFn = (input: IngestSyncInput) =>
  api.post<{ message: string }>('/services/ingest/sync', input, { confirmed: true }).then((r) => r.data);
export const ingestVacuumFn = (input: IngestMaintInput) =>
  api.post<{ message: string }>('/services/ingest/vacuum', input, { confirmed: true }).then((r) => r.data);
export const ingestRefreshMvFn = (input: IngestMaintInput) =>
  api.post<{ message: string }>('/services/ingest/refreshMV', input, { confirmed: true }).then((r) => r.data);
export const fetchJsonServiceInfo = () => api.get<JsonServiceInfo>('/services/json/info').then((r) => r.data);
export const fetchMqttServiceInfo = () => api.get<MqttServiceInfo>('/services/mqtt/info').then((r) => r.data);
// Optimize a CAGG's materialized-hypertable chunks toward ~target_mb. The
// backend resolves the CAGG to its `_materialized_hypertable_N` and forwards it
// schema-qualified to the ingest queue.
export const caggOptimizeFn = (schema: string, name: string, targetMb: number) =>
  api.post<IngestQueued>(`/admin-db/caggs/${schema}/${name}/optimize`, { target_mb: targetMb }, { confirmed: true }).then((r) => r.data);
export const caggMatOnlyFn = (schema: string, name: string, enabled: boolean) =>
  api.post<{ ok: boolean; enabled: boolean }>(`/admin-db/caggs/${schema}/${name}/materialized-only`, { enabled }, { confirmed: true }).then((r) => r.data);

// mesures
export const fetchMesures = () => api.get<MesuresListResponse>('/mesures').then((r) => r.data);
export const fetchMesure  = (id: number) => api.get<Mesure>(`/mesures/${id}`).then((r) => r.data);
export const updateMesureFn = (id: number, input: MesureUpdateInput) =>
  api.put<Mesure>(`/mesures/${id}`, input, { confirmed: true }).then((r) => r.data);
export const fetchMesureWindDirOptions = () =>
  api.get<MesureOption[]>('/mesures/winddir-options').then((r) => r.data);
export const fetchMesureOmmOptions = () =>
  api.get<MesureOption[]>('/mesures/omm-options').then((r) => r.data);

// incidents
export const fetchIncidents = (q: IncidentListQuery = {}) =>
  api.get<IncidentsListResponse>('/incidents', { params: q }).then((r) => r.data);
export const fetchIncidentFacets = () =>
  api.get<IncidentFacets>('/incidents/facets').then((r) => r.data);
export const deactivateIncidentFn = (id: number) =>
  api.patch<Incident>(`/incidents/${id}/deactivate`, undefined, { confirmed: true }).then((r) => r.data);
export const deactivateIncidentsByReasonFn = (reason: string) =>
  api.post<{ affected: number }>('/incidents/deactivate-by-reason', { reason }, { confirmed: true }).then((r) => r.data);
export const purgeInactiveIncidentsFn = () =>
  api.post<{ purged: number }>('/incidents/purge-inactive', { confirm: true }, { confirmed: true }).then((r) => r.data);

// annotations (public.annotations) — CRUD, admin+
export const fetchAnnotations = (q: AnnotationListQuery = {}) =>
  api.get<AnnotationsListResponse>('/annotations', { params: q }).then((r) => r.data);
export const createAnnotationFn = (input: AnnotationWriteInput) =>
  api.post<Annotation>('/annotations', input, { confirmed: true }).then((r) => r.data);
export const updateAnnotationFn = (id: number, input: AnnotationWriteInput) =>
  api.put<Annotation>(`/annotations/${id}`, input, { confirmed: true }).then((r) => r.data);
export const deleteAnnotationFn = (id: number) =>
  api.delete<{ ok: true }>(`/annotations/${id}`, { confirmed: true }).then((r) => r.data);

// upd_weewx (public.update_data) — read-only view + ops proxied to node_climato
export const fetchUpdateData = (q: UpdateDataListQuery = {}) =>
  api.get<UpdateDataListResponse>('/upd-weewx', { params: q }).then((r) => r.data);
export const fetchUpdateDataFacets = () =>
  api.get<UpdateDataFacets>('/upd-weewx/facets').then((r) => r.data);
export const fetchUpdateDataRow = (posteId: number, id: string) =>
  api.get<UpdateDataDetail>(`/upd-weewx/${posteId}/${id}`).then((r) => r.data);
export const processUpdWeewxFn = (meteor?: string) =>
  api.post<{ message: string }>('/upd-weewx/process', meteor ? { meteor } : {}, { confirmed: true }).then((r) => r.data);
export const purgeUpdWeewxFn = (input: { meteor?: string; before_date: string }) =>
  api.post<UpdWeewxPurgeResult>('/upd-weewx/purge', input, { confirmed: true }).then((r) => r.data);

// audit — Log des Mises À Jour (read-only)
export const fetchAuditLogs = (q: AuditLogQuery = {}) =>
  api.get<AuditLogsListResponse>('/audit/logs', { params: q }).then((r) => r.data);
export const fetchAuditFacets = () =>
  api.get<AuditFacets>('/audit/logs/facets').then((r) => r.data);
export const fetchAuditLog = (id: number) =>
  api.get<AuditLogDetail>(`/audit/logs/${id}`).then((r) => r.data);
export const fetchAuthEvents = (q: AuthEventQuery = {}) =>
  api.get<AuthEventsListResponse>('/audit/events', { params: q }).then((r) => r.data);

// users
export const fetchUsers   = () => api.get<UsersListResponse>('/users').then((r) => r.data);
export const updateUserFn = (id: string, input: UserUpdateInput) =>
  api.put<User>(`/users/${id}`, input, { confirmed: true }).then((r) => r.data);
export const createUserFn = (input: UserCreateInput) =>
  api.post<UserCreateResponse>('/users', input, { confirmed: true }).then((r) => r.data);
export const resetPasswordFn = (id: string) =>
  api.post<PasswordResetResponse>(`/users/${id}/reset-password`, undefined, { confirmed: true }).then((r) => r.data);
